import React, { useState, useEffect } from 'react';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import axios from 'axios';
import Cookies from 'js-cookie';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const EmployeeImprovementPlan = () => {
  const base_url = API_BASE_URL;
  const [improvementPlans, setImprovementPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);

  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchImprovementPlans();
  }, []);

  useEffect(() => {
    filterPlans();
  }, [improvementPlans, searchQuery, selectedYear]);

  const fetchImprovementPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get('token');
      const response = await axios.get(`${base_url}/api/improvement-plans/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = Array.isArray(response.data) ? response.data : [];
      if (data.length > 0) {
        setImprovementPlans(data);
        
        // Extract unique years and sort descending
        const years = [...new Set(data
          .map(plan => plan.year)
          .filter(year => year !== null && year !== undefined)
        )].sort((a, b) => b - a);
        
        setAvailableYears(years);
        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
      } else {
        setImprovementPlans([]);
      }
    } catch (err) {
      console.error('Error fetching improvement plans:', err);
      setError('Failed to fetch improvement plans.');
    } finally {
      setLoading(false);
    }
  };

  const filterPlans = () => {
    let filtered = [...improvementPlans];

    // Filter by year
    if (selectedYear) {
      filtered = filtered.filter(plan => 
        plan.year !== null && 
        plan.year !== undefined && 
        plan.year.toString() === selectedYear
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(plan => 
        (plan.employee_name && plan.employee_name.toLowerCase().includes(query)) ||
        (plan.employee_email && plan.employee_email.toLowerCase().includes(query)) ||
        (plan.strengths && plan.strengths.toLowerCase().includes(query)) ||
        (plan.weaknesses && plan.weaknesses.toLowerCase().includes(query)) ||
        (plan.employee_comment && plan.employee_comment.toLowerCase().includes(query)) ||
        (plan.training_needs && plan.training_needs.toLowerCase().includes(query)) ||
        (plan.supervisor_comment && plan.supervisor_comment.toLowerCase().includes(query))
      );
    }

    setFilteredPlans(filtered);
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPlan(null);
  };

  const handleApprove = async () => {
    if (!selectedPlan) return;
    try {
      setSubmitting(true);
      const token = Cookies.get('token');
      await axios.patch(`${base_url}/api/improvement-plans/${selectedPlan.id}/`, {
        supervisor_comment: approvalComment,
        approval_status: 'approved'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setApprovalModalOpen(false);
      setApprovalComment('');
      fetchImprovementPlans();
      closeModal();
    } catch (err) {
      console.error('Error approving plan:', err);
      alert('Failed to approve improvement plan.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPlan) return;
    try {
      setSubmitting(true);
      const token = Cookies.get('token');
      await axios.patch(`${base_url}/api/improvement-plans/${selectedPlan.id}/`, {
        rejection_reason: rejectionReason,
        approval_status: 'rejected'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setRejectionModalOpen(false);
      setRejectionReason('');
      fetchImprovementPlans();
      closeModal();
    } catch (err) {
      console.error('Error rejecting plan:', err);
      alert('Failed to reject improvement plan.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="badge bg-success-transparent text-success d-inline-flex align-items-center gap-1 fs-12 py-1 px-2">
            <i className="ti ti-circle-check-filled"></i> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="badge bg-danger-transparent text-danger d-inline-flex align-items-center gap-1 fs-12 py-1 px-2">
            <i className="ti ti-circle-x-filled"></i> Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="badge bg-warning-transparent text-warning d-inline-flex align-items-center gap-1 fs-12 py-1 px-2">
            <i className="ti ti-clock"></i> Pending Review
          </span>
        );
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2 mb-md-0">
              <h4 className="mb-1">Employee Improvement Plans</h4>
              <p className="text-muted mb-0 fs-13">Review and manage employee improvement plans</p>
            </div>
          </div>

          {/* Card Container */}
          <div className="card shadow-sm border-0 mb-4">
            {/* Filter Section */}
            <div className="card-header bg-white py-3 border-bottom">
              <div className="row g-3 align-items-center justify-content-between">
                <div className="col-md-6 col-lg-5">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="ti ti-search fs-16 text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search by employee name, email, or content..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-4 col-lg-3 d-flex align-items-center justify-content-md-end gap-2">
                  <label className="form-label mb-0 fs-14 fw-medium text-nowrap">Year:</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {availableYears.length === 0 ? (
                      <option value="">No years available</option>
                    ) : (
                      availableYears.map(year => (
                        <option key={year} value={year.toString()}>
                          {year}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Content Body */}
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading improvement plans...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-3 d-flex align-items-center justify-content-between">
                  <span>{error}</span>
                  <button onClick={fetchImprovementPlans} className="btn btn-sm btn-outline-danger">
                    Try Again
                  </button>
                </div>
              ) : filteredPlans.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ti ti-message-dots fs-36 text-muted mb-2"></i>
                  <h5 className="fw-semibold mb-1">No Improvement Plans Found</h5>
                  <p className="text-muted fs-13 mb-0">
                    {searchQuery || selectedYear
                      ? 'Try adjusting your search criteria or year filter.'
                      : 'No improvement plans have been submitted yet.'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Year</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlans.map((plan) => (
                        <tr key={plan.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-md bg-light-subtle rounded-circle d-flex align-items-center justify-content-center text-primary">
                                <i className="ti ti-user fs-18"></i>
                              </div>
                              <div>
                                <div className="fw-semibold text-dark fs-14">
                                  {plan.employee_name || 'Unknown Employee'}
                                </div>
                                <div className="text-muted fs-12">
                                  {plan.employee_email || 'No email provided'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="fw-medium">
                            <span className="badge bg-light text-dark border">
                              <i className="ti ti-calendar me-1"></i> {plan.year || 'N/A'}
                            </span>
                          </td>
                          <td>{renderStatusBadge(plan.approval_status)}</td>
                          <td className="text-center">
                            <button
                              onClick={() => handleViewPlan(plan)}
                              className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1"
                              title="View full details"
                            >
                              <i className="ti ti-eye fs-16 text-muted"></i>
                              <span>View</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Details Modal */}
      {modalOpen && selectedPlan && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="modal-title fw-bold mb-1">Improvement Plan Details</h5>
                  <div className="d-flex align-items-center gap-2 text-muted fs-13">
                    <i className="ti ti-user fs-15"></i>
                    <span>{selectedPlan.employee_name || 'Unknown Employee'}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {selectedPlan.approval_status === 'pending' && (
                    <>
                      <button
                        onClick={() => setApprovalModalOpen(true)}
                        className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
                        title="Approve plan"
                      >
                        <i className="ti ti-check fs-16"></i>
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => setRejectionModalOpen(true)}
                        className="btn btn-sm btn-danger d-inline-flex align-items-center gap-1"
                        title="Reject plan"
                      >
                        <i className="ti ti-x fs-16"></i>
                        <span>Reject</span>
                      </button>
                    </>
                  )}
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
              </div>

              <div className="modal-body">
                {/* Meta summary badges */}
                <div className="d-flex align-items-center gap-3 p-3 bg-light rounded mb-4">
                  <div className="d-flex align-items-center gap-1 text-dark fw-medium">
                    <i className="ti ti-calendar text-primary fs-16"></i>
                    <span>Year: {selectedPlan.year || 'N/A'}</span>
                  </div>
                  <div>
                    {renderStatusBadge(selectedPlan.approval_status)}
                  </div>
                </div>

                {/* Rejection notice if rejected */}
                {selectedPlan.approval_status === 'rejected' && selectedPlan.rejection_reason && (
                  <div className="alert alert-danger mb-4">
                    <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                      <i className="ti ti-alert-circle fs-18"></i>
                      <span>Rejection Reason</span>
                    </h6>
                    <p className="mb-0 fs-14">{selectedPlan.rejection_reason}</p>
                  </div>
                )}

                {/* Supervisor comment if approved */}
                {selectedPlan.approval_status === 'approved' && selectedPlan.supervisor_comment && (
                  <div className="alert alert-success mb-4">
                    <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                      <i className="ti ti-circle-check fs-18"></i>
                      <span>Supervisor Comment</span>
                    </h6>
                    <p className="mb-0 fs-14">{selectedPlan.supervisor_comment}</p>
                  </div>
                )}

                {/* Detailed content sections */}
                <div className="row g-3">
                  <div className="col-12">
                    <div className="border rounded p-3 bg-white">
                      <h6 className="fw-bold text-dark border-bottom pb-2 mb-2">Strengths</h6>
                      <p className="mb-0 fs-14 text-secondary">{selectedPlan.strengths || 'No strengths listed'}</p>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="border rounded p-3 bg-white">
                      <h6 className="fw-bold text-dark border-bottom pb-2 mb-2">Areas for Improvement</h6>
                      <p className="mb-0 fs-14 text-secondary">{selectedPlan.weaknesses || 'No areas for improvement listed'}</p>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="border rounded p-3 bg-white">
                      <h6 className="fw-bold text-dark border-bottom pb-2 mb-2">Training Needs</h6>
                      <p className="mb-0 fs-14 text-secondary">{selectedPlan.training_needs || 'No training needs specified'}</p>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="border rounded p-3 bg-white">
                      <h6 className="fw-bold text-dark border-bottom pb-2 mb-2">Employee Comments</h6>
                      <p className="mb-0 fs-14 text-secondary">{selectedPlan.employee_comment || 'No comment provided'}</p>
                    </div>
                  </div>

                  {selectedPlan.date_signed && (
                    <div className="col-12">
                      <div className="border rounded p-3 bg-white">
                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-2">Date Signed</h6>
                        <p className="mb-0 fs-14 text-secondary">{formatDate(selectedPlan.date_signed)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {approvalModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Approve Improvement Plan</h5>
                <button type="button" className="btn-close" onClick={() => setApprovalModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="approval-comment" className="form-label fw-medium">
                    Supervisor Comment <span className="text-muted fw-normal">(Optional)</span>
                  </label>
                  <textarea
                    id="approval-comment"
                    className="form-control"
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder="Add any additional comments for the employee..."
                    rows="4"
                    disabled={submitting}
                  />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setApprovalModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  className="btn btn-success d-flex align-items-center gap-2"
                  disabled={submitting}
                >
                  {submitting && <span className="spinner-border spinner-border-sm"></span>}
                  <span>Approve Plan</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Reject Improvement Plan</h5>
                <button type="button" className="btn-close" onClick={() => setRejectionModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="rejection-reason" className="form-label fw-medium">
                    Reason for Rejection <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="rejection-reason"
                    className="form-control"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide specific reasons for rejection..."
                    rows="4"
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setRejectionModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  className="btn btn-danger d-flex align-items-center gap-2"
                  disabled={submitting || !rejectionReason.trim()}
                >
                  {submitting && <span className="spinner-border spinner-border-sm"></span>}
                  <span>Reject Plan</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeImprovementPlan;